const API_URL = process.env.REACT_APP_API_URL;

/**
 * Obtiene las preguntas dinámicas activas sanitizadas para la vista del aficionado.
 */
const GetTriviaQuestions = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/trivia`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener las preguntas de la trivia.' };
        }
        const data = await response.json();
        return { success: true, data: data.data || [] };
    } catch (error) {
        console.error("Error en GetTriviaQuestions:", error);
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

/**
 * Envía las respuestas del aficionado para su evaluación atómica.
 */
const SubmitTrivia = async (payload) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { 
                success: false, 
                message: resData.message || 'Error al enviar la trivia.' 
            };
        }
        return resData;
    } catch (error) {
        console.error("Error en SubmitTrivia:", error);
        return { success: false, message: 'Error de conexión al enviar la trivia.' };
    }
};

/**
 * Registra eventos de telemetría del embudo (escaneo, inicio de formulario, etc.).
 */
const TrackMetric = async (eventName, sessionId = null, source = null) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/metric`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ eventName, sessionId, source })
        });
        if (!response.ok) return { success: false };
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false };
    }
};

/**
 * Consulta la configuración pública o del partido.
 */
const GetPublicConfig = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) return { success: false };
        const data = await response.json();
        return { success: true, data: data.data };
    } catch (error) {
        return { success: false };
    }
};

/**
 * Consulta y recupera un pase ganado por correo o teléfono con Rate Limiting.
 */
const RecoverPass = async (query) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/recover-pass`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query })
        });
        const resData = await response.json().catch(() => ({}));
        if (response.status === 429) {
            return {
                success: false,
                isRateLimited: true,
                waitSeconds: resData.waitSeconds || 60,
                message: resData.message || 'Límite de consultas excedido. Por favor espera un momento.'
            };
        }
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al consultar el pase.'
            };
        }
        return resData;
    } catch (error) {
        console.error("Error en RecoverPass:", error);
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

/**
 * Obtiene la configuración completa para el panel administrativo.
 */
const GetAdminConfig = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en GetAdminConfig:", error);
        return { success: false, message: 'Error al obtener la configuración administrativa.' };
    }
};

/**
 * Actualiza la configuración de la dinámica.
 */
const UpdateAdminConfig = async (payload) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en UpdateAdminConfig:", error);
        return { success: false, message: 'Error al actualizar la configuración.' };
    }
};

/**
 * Obtiene el listado de preguntas con respuestas y opciones para el administrador.
 */
const GetAdminQuestions = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/questions`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en GetAdminQuestions:", error);
        return { success: false, message: 'Error al obtener las preguntas para administración.' };
    }
};

/**
 * Guarda (crea o edita) una pregunta con sus opciones.
 */
const SaveAdminQuestion = async (payload) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/questions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en SaveAdminQuestion:", error);
        return { success: false, message: 'Error al guardar la pregunta.' };
    }
};

/**
 * Elimina una pregunta por ID.
 */
const DeleteAdminQuestion = async (id) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/questions/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en DeleteAdminQuestion:", error);
        return { success: false, message: 'Error al eliminar la pregunta.' };
    }
};

/**
 * Obtiene los participantes con filtros y búsqueda.
 */
const GetAdminParticipants = async (filter = '', search = '') => {
    try {
        const params = new URLSearchParams();
        if (filter) params.append('filter', filter);
        if (search) params.append('search', search);

        const response = await fetch(`${API_URL}QuizzB9Zone/admin/participants?${params.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en GetAdminParticipants:", error);
        return { success: false, message: 'Error al obtener la lista de participantes.' };
    }
};

/**
 * Obtiene las estadísticas de embudo y estado de pases.
 */
const GetAdminStats = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/stats`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en GetAdminStats:", error);
        return { success: false, message: 'Error al obtener estadísticas.' };
    }
};

/**
 * Descalifica a un participante.
 */
const DisqualifyParticipant = async (participantId, reason, operator = null) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/disqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, reason, operator })
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en DisqualifyParticipant:", error);
        return { success: false, message: 'Error al descalificar al participante.' };
    }
};

/**
 * Remueve la descalificación de un participante.
 */
const UndisqualifyParticipant = async (participantId, operator = null) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/undisqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, operator })
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en UndisqualifyParticipant:", error);
        return { success: false, message: 'Error al remover la descalificación.' };
    }
};

/**
 * Reinicia la dinámica por completo.
 */
const ResetDynamic = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en ResetDynamic:", error);
        return { success: false, message: 'Error al reiniciar la dinámica.' };
    }
};

/**
 * Obtiene el conteo de ganadores en tiempo real.
 */
const GetWinnerCount = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/winner-count`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en GetWinnerCount:", error);
        return { success: false, message: 'Error al obtener conteo de ganadores.' };
    }
};

/**
 * Realiza el check-in de un pase escaneado en el acceso a cancha (Túnel 2).
 */
const CheckinParticipant = async (scanData, operator = null) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/checkin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scanData, operator })
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en CheckinParticipant:", error);
        return { success: false, status: "ERROR", message: 'Error de conexión al validar el pase.' };
    }
};

/**
 * Actualiza la visibilidad del overlay en tiempo real (OBS / Pantallas).
 * @param {boolean} isVisible
 */
const SetOverlayVisibilityDL = async (isVisible) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/overlay-visibility`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isVisible })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return await UpdateAdminConfig({ isOverlayVisible: isVisible });
        }
        return data;
    } catch (error) {
        console.error("Error en SetOverlayVisibilityDL:", error);
        return await UpdateAdminConfig({ isOverlayVisible: isVisible });
    }
};

/**
 * Actualiza el offset vertical del overlay en tiempo real (OBS / Pantallas).
 * @param {number} offsetY
 */
const SetOverlayOffsetDL = async (offsetY) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/overlay-config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ offsetY })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return await UpdateAdminConfig({ offsetY });
        }
        return data;
    } catch (error) {
        console.error("Error en SetOverlayOffsetDL:", error);
        return await UpdateAdminConfig({ offsetY });
    }
};

/**
 * Verifica si un aficionado ya participó previamente por correo, teléfono o nombre.
 */
const CheckDuplicateParticipant = async (email, phone, name) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/check-duplicate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, phone, name })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al verificar duplicidad.' };
        }
        const data = await response.json();
        return { success: true, exists: Boolean(data.exists) };
    } catch (error) {
        console.error("Error en CheckDuplicateParticipant:", error);
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

/**
 * Registra la captura de pantalla o descarga del pase realizada por el aficionado.
 */
const LogScreenshot = async (participantId, passCode, name, method) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/log-screenshot`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, passCode, name, method })
        });
        if (!response.ok) return { success: false };
        const data = await response.json().catch(() => ({}));
        return { success: true, data };
    } catch (error) {
        console.error("Error en LogScreenshot:", error);
        return { success: false };
    }
};

/**
 * Obtiene el dataset completo administrativo para exportación multi-hoja a Excel.
 */
const GetExportDataset = async () => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/admin/export-dataset`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener datos de exportación.' };
        }
        const data = await response.json();
        return { success: true, data: data.data };
    } catch (error) {
        console.error("Error en GetExportDataset:", error);
        return { success: false, message: 'Error de conexión con el servidor.' };
    }
};

/**
 * Sube una imagen o medio para QuizzB9Zone (Cintillos, Logos, etc).
 */
const UploadMedia = async (formData) => {
    try {
        const response = await fetch(`${API_URL}QuizzB9Zone/UploadMedia`, {
            method: 'POST',
            body: formData
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en UploadMedia QuizzB9Zone:", error);
        return { success: false, message: 'Error de conexión al subir la imagen.' };
    }
};

export {
    GetTriviaQuestions,
    SubmitTrivia,
    CheckDuplicateParticipant,
    LogScreenshot,
    GetExportDataset,
    TrackMetric,
    GetPublicConfig,
    RecoverPass,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminQuestions,
    SaveAdminQuestion,
    DeleteAdminQuestion,
    GetAdminParticipants,
    GetAdminStats,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    ResetDynamic,
    GetWinnerCount,
    CheckinParticipant,
    SetOverlayVisibilityDL,
    SetOverlayOffsetDL,
    UploadMedia
};



